import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Carousel } from 'antd';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HomeIcon from '@mui/icons-material/Home';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import DomainIcon from '@mui/icons-material/Domain';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StoreIcon from '@mui/icons-material/Store';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import WorkIcon from '@mui/icons-material/Work';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HotelIcon from '@mui/icons-material/Hotel';
import VillaIcon from '@mui/icons-material/Villa';
import FactoryIcon from '@mui/icons-material/Factory';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import categoryData from '../../utils/data/category.json';

// Styled components
const CategoryItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: '116px',
  height: '57px',
  margin: '0',
  cursor: 'pointer',
  width: '100%',
});

const IconContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '4px',
  width: '147px',
});

const CategoryLabel = styled(Typography)({
  fontSize: '19px',
  textAlign: 'center',
  lineHeight: 1.2,
  fontWeight: 400,
  color: '#333',
  overflow: 'hidden',
  width: '147px',
  // textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const CustomCarousel = styled(Carousel)({
  '.slick-dots': {
    bottom: '-25px',
  },
  '.slick-dots li button': {
    backgroundColor: 'rgba(211, 211, 211, 1)',
    borderRadius: '50%',
    width: '10px',
    height: '10px',
    '&:before': {
      content: '""',
    },
  },
  '.slick-dots li.slick-active button': {
    backgroundColor: 'rgba(244, 118, 42, 1)',
    width: '10px',
    height: '10px',
  },
  '.slick-slide > div': {
    display: 'flex',
    justifyContent: 'center',
  },
});

// Icons mapping
const getCategoryIcon = (categoryID) => {
  const iconProps = { style: { fontSize: 20 } };
  
  const iconMap = {
    1: <ApartmentIcon {...iconProps} />, // คอนโด
    3: <HomeIcon {...iconProps} />, // บ้านเดี่ยว
    2: <HomeWorkIcon {...iconProps} />, // ทาวน์เฮาส์-โฮม
    24: <DomainIcon {...iconProps} />, // บ้านแฝด
    4: <BusinessIcon {...iconProps} />, // โฮมออฟฟิศ
    5: <LocationOnIcon {...iconProps} />, // ที่ดิน
    9: <StoreIcon {...iconProps} />, // อาคารพาณิชย์-ตึกแถว
    7: <CorporateFareIcon {...iconProps} />, // อพาร์ทเมนท์
    25: <VillaIcon {...iconProps} />, // บ้านหรู-คฤหาสน์
    10: <StoreIcon {...iconProps} />, // พื้นที่
    11: <HotelIcon {...iconProps} />, // โรงแรม
    17: <BeachAccessIcon {...iconProps} />, // รีสอร์ท
    16: <FactoryIcon {...iconProps} />, // โรงงาน
    8: <WarehouseIcon {...iconProps} />, // โกดัง-คลังสินค้า
    6: <WorkIcon {...iconProps} />, // สำนักงาน
  };
  
  return iconMap[categoryID] || <ApartmentIcon {...iconProps} />;
};

const CategoriesCarousel = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = router;
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // ใช้ข้อมูลจาก category.json
    if (categoryData && categoryData.data) {
      // เรียงลำดับตาม category_Sort
      const sortedCategories = [...categoryData.data].sort((a, b) => a.category_Sort - b.category_Sort);
      setCategories(sortedCategories);
    }
  }, []);

  const carouselSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 10,
    slidesToScroll: 5,
    initialSlide: 0,
    draggable: true,
    swipe: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        }
      }
    ]
  };

  const handleCategoryClick = (category) => {
    console.log(`Selected category: ${category.category_ID}`);
    
    // ขึ้นอยู่กับภาษาปัจจุบัน
    let actionText = 'ขาย';
    let categoryName = category[`category_Name_TH`];
    
    if (locale === 'en') {
      actionText = 'sell';
      categoryName = category[`category_Name_EN`];
    } else if (locale === 'cn') {
      actionText = '售';
      categoryName = category[`category_Name_CN`];
    }
    
    // สร้าง URL ในรูปแบบ /ขาย/{category}?endPrice=1000000000
    const url = `/${actionText}/${categoryName}?endPrice=1000000000`;
    router.push(url);
  };

  return (
    <Box sx={{ 
      maxWidth: '1496px', 
      margin: '0 auto 20px', 
      py: 3,
      px: 2,
    }}>
      <CustomCarousel {...carouselSettings}>
        {categories.map((category) => (
          <CategoryItem key={category.category_ID} onClick={() => handleCategoryClick(category)}>
            <IconContainer>
              {getCategoryIcon(category.category_ID)}
            </IconContainer>
            <CategoryLabel>
              {locale === 'en' 
                ? category.category_Name_EN 
                : locale === 'cn' 
                  ? category.category_Name_CN 
                  : category.category_Name_TH}
            </CategoryLabel>
          </CategoryItem>
        ))}
      </CustomCarousel>
    </Box>
  );
};

export default CategoriesCarousel;